import { Box, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomDialog } from "../../../../global/components";
import CustomTabs from "../../../../global/components/CustomTabs/CustomTabs1";
import strings from "../../../../global/constants/StringConstants";
import {
  formatDateTime,
  formatDateToLongString,
  handleErrorMessage,
} from "../../../../helpers/methods";
import {
  selectFirstName,
  selectLatitude,
  selectLongitude,
} from "../../../../redux/authSlice";
import { useAppSelector } from "../../../../utils/hooks";
import { theme } from "../../../../utils/styles";
import PremiumContentWrapper from "../../PremiumFeature/PremiumContentWrapper";
import rahuTransitDialogStyles from "./RahuTransitDialog.styles";
import { getRahuTransits } from "./RahuTransitDialogServices";

interface CustomProps {
  setIsLoading: Function;
  handleClose: Function;
  open: boolean;
  isLoading: boolean;
}

const RahuTransitDialog = (props: CustomProps) => {
  const classes = rahuTransitDialogStyles;
  const { t, i18n } = useTranslation();
  const username = useAppSelector(selectFirstName);
  const userLatitude = useAppSelector(selectLatitude);
  const userLongitude = useAppSelector(selectLongitude);
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [tabValue, setTabValue] = useState(`${t("zodiacTab")}`);
  const [rahuTransitData, setRahuTransitData] = useState({
    globalResponse: [
      {
        datetime: "",
        from_sign: "",
        to_sign: "",
        type: "",
        nakshtra_name: "",
        pada: 0,
        pada_lord: "",
        pada_sign: "",
      },
      {
        datetime: "",
        from_sign: "",
        to_sign: "",
        type: "",
        nakshtra_name: "",
        pada: 0,
        pada_lord: "",
        pada_sign: "",
      },
    ],
    nakshtra: [],
    pada: [],
  });

  const tabConfig = [
    {
      label: `${t("zodiacTab")}`,
    },
    {
      label: `${t("nakshatraTab")}`,
    },
    {
      label: `${t("padaTab")}`,
    },
  ];

  useEffect(() => {
    if (props.open) {
      getRahuTransitsData();
    }
  }, [props.open, i18n.language]);

  const getRahuTransitsData = async () => {
    try {
      props.setIsLoading(true);
      const obj = {
        date,
        latitude: +userLatitude,
        longitude: +userLongitude,
      };
      const language = i18n.language.toLowerCase();
      const response = await getRahuTransits(obj, language);
      setRahuTransitData(response);
      props.setIsLoading(false);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleChange = (newValue: string) => {
    setTabValue(newValue);
  };

  const getNakshatraData = () => {
    const meanNakshatra: any[] = [];
    const trueNakshatra: any[] = [];

    rahuTransitData.nakshtra.forEach((item: any) => {
      if (item.type === "Mean Rahu") {
        meanNakshatra.push(item);
      } else {
        trueNakshatra.push(item);
      }
    });

    return { meanNakshatra, trueNakshatra };
  };

  const getPadaData = () => {
    const meanPada: any[] = [];
    const truePada: any[] = [];

    rahuTransitData.pada.forEach((padaArray: any[]) => {
      padaArray.forEach((item: any) => {
        if (item.type === "Mean Rahu") {
          meanPada.push(item);
        } else {
          truePada.push(item);
        }
      });
    });

    return { meanPada, truePada };
  };

  const dialogHeaderContent = () => {
    return (
      <Stack direction={"column"} alignItems={"center"} spacing={2} pt={5}>
        <Typography sx={classes.modalHeader} px={2}>
          {t("global")}
        </Typography>
        <Divider
          sx={{
            border: "1px solid #222222",
            width: "50%",
            height: "0px",
          }}
        />
      </Stack>
    );
  };

  const getGlobalRahuTransit = () => {
    return (
      <Stack direction={"column"} spacing={1}>
        <Grid container>
          {rahuTransitData.globalResponse.map((item, index) => (
            <Grid
              item
              xl={6}
              lg={6}
              md={12}
              sm={12}
              xs={12}
              p={1}
              key={`current-${index}`}
            >
              <Box sx={classes.transitBox}>
                <Typography sx={classes.contentText}>
                  {item.type === "Mean Rahu"
                    ? t("rahuTranisiting")
                    : `True ${t("rahuTranisiting")}`}{" "}
                  <b>{item.from_sign}</b> on{" "}
                  {formatDateToLongString(date, i18n.language)}
                </Typography>
              </Box>
            </Grid>
          ))}
          {rahuTransitData.globalResponse.map((item, index) => (
            <Grid
              item
              xl={6}
              lg={6}
              md={12}
              sm={12}
              xs={12}
              p={1}
              key={`next-${index}`}
            >
              <Box sx={classes.transitBox}>
                {props.isLoading ? (
                  <>
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={50}
                      sx={{ m: 1 }}
                    />
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={50}
                      sx={{ m: 1 }}
                    />
                  </>
                ) : (
                  <>
                    <Stack
                      direction={"column"}
                      alignItems={"center"}
                      spacing={2}
                      pt={1}
                    >
                      <Typography sx={classes.transitHeader} px={2}>
                        {item.type === "Mean Rahu"
                          ? t("rahuTransit")
                          : t("trueRahuTransit")}
                      </Typography>
                      <Divider
                        sx={{
                          border: "1px solid #767676",
                          width: "50%",
                          height: "0px",
                          mt: "2px !important",
                          mb: "8px !important",
                        }}
                      />
                    </Stack>
                    <Typography my={1} mx={1} sx={classes.contentText}>
                      From <b>{item.from_sign}</b> to <b>{item.to_sign}</b>{" "}
                    </Typography>
                    <Typography mb={1} mx={1} sx={classes.contentText}>
                      {formatDateTime(item.datetime, i18n.language)}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  };

  const getNakshatraContent = () => {
    const { meanNakshatra, trueNakshatra } = getNakshatraData();

    const renderNakshatraList = (items: any[], title: string) => (
      <Grid item xl={6} lg={6} md={12} sm={12} xs={12} p={1}>
        <Stack direction={"column"} alignItems={"center"} spacing={2} pb={1}>
          <Typography sx={classes.transitHeader} px={2}>
            {title}
          </Typography>
          <Divider
            sx={{
              border: "1px solid #767676",
              width: "50%",
              height: "0px",
              mt: "2px !important",
              mb: "8px !important",
            }}
          />
        </Stack>
        <Stack direction={"column"} alignItems={"center"} spacing={2}>
          {items.map((item: any, index: number) => (
            <Box sx={classes.transitBox} key={index}>
              {props.isLoading ? (
                <>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={50}
                    sx={{ m: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={50}
                    sx={{ m: 1 }}
                  />
                </>
              ) : (
                <>
                  <Typography my={1} mx={1} sx={classes.contentText}>
                    From <b>{item.from_nakshatra}</b> to{" "}
                    <b>{item.to_nakshatra}</b>
                  </Typography>
                  <Typography mb={1} mx={1} sx={classes.contentText}>
                    {formatDateTime(item.datetime, i18n.language)}
                  </Typography>
                </>
              )}
            </Box>
          ))}
        </Stack>
      </Grid>
    );

    return (
      <Grid container>
        {renderNakshatraList(meanNakshatra, t("rahuTransit"))}
        {renderNakshatraList(trueNakshatra, t("trueRahuTransit"))}
      </Grid>
    );
  };

  const getPadaContent = () => {
    const { meanPada, truePada } = getPadaData();

    const renderPadaList = (items: any[], title: string) => (
      <Grid item xl={6} lg={6} md={12} sm={12} xs={12} p={1}>
        <Stack direction={"column"} alignItems={"center"} spacing={2} pb={1}>
          <Typography sx={classes.transitHeader} px={2}>
            {title}
          </Typography>
          <Divider
            sx={{
              border: "1px solid #767676",
              width: "50%",
              height: "0px",
              mt: "2px !important",
              mb: "8px !important",
            }}
          />
        </Stack>
        <Stack direction={"column"} alignItems={"center"} spacing={2}>
          {items.map((item: any, index: number) => (
            <Box sx={classes.transitBox} key={index}>
              {props.isLoading ? (
                <>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={30}
                    sx={{ m: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={30}
                    sx={{ m: 1 }}
                  />
                </>
              ) : (
                <>
                  <Typography my={1} mx={1} sx={classes.contentText}>
                    From <b>{item.from_pada}</b> to <b>{item.to_pada}</b>
                  </Typography>
                  <Typography mb={1} mx={1} sx={classes.contentText}>
                    {formatDateTime(item.datetime, i18n.language)}
                  </Typography>
                </>
              )}
            </Box>
          ))}
        </Stack>
      </Grid>
    );

    return (
      <Grid container>
        {renderPadaList(meanPada, t("rahuTransit"))}
        {renderPadaList(truePada, t("trueRahuTransit"))}
      </Grid>
    );
  };

  const getContentBasedOnTab = () => {
    switch (tabValue) {
      case `${t("zodiacTab")}`:
        return getGlobalRahuTransit();
      case `${t("nakshatraTab")}`:
        return getNakshatraContent();
      case `${t("padaTab")}`:
        return getPadaContent();
      default:
        return getGlobalRahuTransit();
    }
  };

  const getDatePickerContent = () => {
    return (
      <Stack
        direction={{ sm: "row", xs: "column" }}
        spacing={{ sm: 2, xs: 1 }}
        justifyContent={"center"}
        mb={2}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={dayjs(date, "YYYY-MM-DD")}
            onChange={(newValue: any) => {
              const formattedDate = newValue.format("YYYY-MM-DD");
              setDate(formattedDate);
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
        <CustomButton
          label={t("calculate")}
          type={strings.PRIMARY}
          onClick={getRahuTransitsData}
          customClasses={{
            width: "fit-content",
            [theme.breakpoints.down("sm")]: {
              fontSize: "14px",
              py: "6px",
              width: "100%",
            },
          }}
        />
      </Stack>
    );
  };

  const dialogBodyContent = () => {
    const inActiveTabStyle = {
      backgroundColor: "#F5F5F5",
      color: "#666666",
      border: "1px solid #E0E0E0",
    };

    const activeTabStyle = {
      backgroundColor: "#9053f6",
      color: "#F9F9F9",
    };

    return (
      <PremiumContentWrapper setIsLoading={props.setIsLoading}>
        <Box>
          <Stack direction={"column"} spacing={2} mb={2}>
            {[1, 2].map((desc) => {
              return (
                <Box key={desc} sx={classes.legendDescText}>
                  {t(`globalRahuTransitDesc${desc}`)}
                </Box>
              );
            })}
          </Stack>
          {getDatePickerContent()}
          <Box py={2.5}>
            <CustomTabs
              changeValue={handleChange}
              selected={tabValue}
              tabConfig={tabConfig}
              activeTabStyle={activeTabStyle}
              inActiveTabStyle={inActiveTabStyle}
            />
          </Box>
          {getContentBasedOnTab()}
        </Box>
      </PremiumContentWrapper>
    );
  };

  const getRahuTransitDialog = () => {
    return (
      <CustomDialog
        width="1000px"
        borderRadius="10px"
        isDialogOpen={props.open}
        closable={true}
        closeButtonVisibility={true}
        handleDialogClose={() => props.handleClose()}
        dialogHeaderContent={dialogHeaderContent()}
        dialogBodyContent={dialogBodyContent()}
      />
    );
  };
  return getRahuTransitDialog();
};

export default RahuTransitDialog;
